import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Gift, Copy, Check } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function ReferralSection({ darkMode }) {
  const [referral, setReferral] = useState(null);
  const [history, setHistory] = useState([]);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };
    axios.get(`${API}/referral/code`, { headers }).then(r => setReferral(r.data)).catch(() => {});
    axios.get(`${API}/referral/history`, { headers }).then(r => setHistory(r.data)).catch(() => {});
  }, []);

  const copyCode = () => {
    if (referral?.code) {
      navigator.clipboard.writeText(referral.code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className={`rounded-xl p-6 ${darkMode ? 'bg-slate-800/50 border border-slate-700' : 'bg-white border border-slate-200 shadow-sm'}`}>
      <div className="flex items-center gap-3 mb-5">
        <div className="p-2 rounded-lg bg-purple-100">
          <Gift className="w-5 h-5 text-purple-600" />
        </div>
        <h2 className={`text-xl font-heading font-bold ${darkMode ? 'text-slate-100' : 'text-slate-800'}`}>Referral Program</h2>
      </div>

      {referral && (
        <div className="space-y-4">
          <div className={`p-4 rounded-lg ${darkMode ? 'bg-slate-700/50' : 'bg-slate-50'} flex items-center justify-between`}>
            <div>
              <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Your Referral Code</p>
              <p className={`text-2xl font-mono font-bold tracking-wider ${darkMode ? 'text-white' : 'text-slate-800'}`}>{referral.code}</p>
            </div>
            <Button
              data-testid="copy-referral-code"
              onClick={copyCode}
              variant="outline"
              size="sm"
              className={darkMode ? 'border-slate-600 text-slate-300' : ''}
            >
              {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
              {copied ? 'Copied!' : 'Copy'}
            </Button>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className={`p-3 rounded-lg text-center ${darkMode ? 'bg-slate-700/30' : 'bg-blue-50'}`}>
              <p className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-blue-700'}`}>{referral.converted_count || 0}</p>
              <p className={`text-xs ${darkMode ? 'text-slate-400' : 'text-blue-600'}`}>Conversions</p>
            </div>
            <div className={`p-3 rounded-lg text-center ${darkMode ? 'bg-slate-700/30' : 'bg-green-50'}`}>
              <p className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-green-700'}`}>${(referral.total_credits_earned || 0).toFixed(2)}</p>
              <p className={`text-xs ${darkMode ? 'text-slate-400' : 'text-green-600'}`}>Credits Earned</p>
            </div>
            <div className={`p-3 rounded-lg text-center ${darkMode ? 'bg-slate-700/30' : 'bg-purple-50'}`}>
              <p className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-purple-700'}`}>${(referral.pending_credits || 0).toFixed(2)}</p>
              <p className={`text-xs ${darkMode ? 'text-slate-400' : 'text-purple-600'}`}>Pending</p>
            </div>
          </div>

          <div className={`p-4 rounded-lg ${darkMode ? 'bg-indigo-900/30 border border-indigo-800' : 'bg-indigo-50 border border-indigo-100'}`}>
            <p className={`text-sm font-medium ${darkMode ? 'text-indigo-300' : 'text-indigo-700'}`}>How it works</p>
            <p className={`text-xs mt-1 ${darkMode ? 'text-indigo-400' : 'text-indigo-600'}`}>
              Share your referral code with other dental practices. When they subscribe using your code,
              you'll receive a credit equivalent to 1 month free on their plan!
            </p>
          </div>

          {history.length > 0 && (
            <div>
              <h3 className={`text-sm font-semibold mb-2 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>Referral History</h3>
              <div className="space-y-2">
                {history.map((h, i) => (
                  <div key={i} className={`flex justify-between items-center p-2 rounded ${darkMode ? 'bg-slate-700/30' : 'bg-slate-50'}`}>
                    <span className={`text-sm ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>{h.description}</span>
                    <span className={`text-sm font-medium ${darkMode ? 'text-green-400' : 'text-green-600'}`}>+${(h.amount_usd || 0).toFixed(2)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
